import { FullReportEntry } from "../models/FullReportEntry";
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Loading } from "../components/Loading";
import { loadChannelReportByAccount } from "./channel-store";
import { AccountChannelReport, ChannelColor, ChannelLabel, ChannelReport, channels, ChannelType } from "./channel-models";
import { LineChart, Serie, SpotItem } from "react-charts";
import { monthName, parseDate } from "simple-utils/dist/date-utils";
import { Summary } from "../ads-procedure-report-page/procedure-models";
import { bigNumber, formatMoney } from "../utils/money-utils";
import { sum } from "simple-utils";
import { TypeCounter } from "../ads-detail-page/AdsDetailPage";
import { AccountContext } from "../account/AccountContext";
import { PeriodLabel } from "../account/PeriodSelector";
import { getAccountEventInfo, logEvent } from "../ga/ga-event-logger";


type AccountChannelReportDate = {
    date: Date
    report: ChannelReport   
}

function asAccountDateReport(report: AccountChannelReport){
    const list = Object.entries(report).map(([date, report]) => 
        ({date: parseDate(date), report} as AccountChannelReportDate)
    )
    return list.filter(i => i.date != null)
}

type ColGetter = (c:Summary) => number

function buildChannelSerie(list: AccountChannelReportDate[], channel: ChannelType, key: (keyof Summary) | ColGetter):Serie{
    var fn = typeof key == 'function' ? key : (k:Summary)=> k?.[key]
    
    return {
        style: ChannelColor[channel],
        label: ChannelLabel[channel],
        data: list.map(d => ({x: d.date.getTime(), date: d.date, y: fn(d.report?.[channel]) ?? 0}))
    }
}

function createTooltipDate(e: SpotItem[], label: string, numberFormat:(n:number) => string){
    if(e == null) return null;
    if(e.length == 0) return null;
    return <>
        <div className="mb-1">        
            <b>{label}</b><br />
            {xLabel(e[0].item.x)}
        </div>
        {
           e.map( (i, n) => <div className={i.serie.style} key={n}>{i.serie.label}: <b className="text-color">{numberFormat(i.item.y)}</b></div>) 
        }
    </>
}

const leadTooltip = (e: SpotItem[]) => createTooltipDate(e, "Leads", bigNumber)
const costTooltip = (e: SpotItem[]) => createTooltipDate(e, "Investimento", formatMoney)
const cplTooltip = (e: SpotItem[]) => createTooltipDate(e, "CPL", formatMoney)


function xLabel(n:number){
    var d = new Date();
    d.setTime(n);
    return d.getDate() + "/" + monthName(d, true);
}

export function TypeSummary({report, showOther = true, numLeads = null}:{report: ChannelReport, showOther?:boolean, numLeads?:number}){
    var wa = useMemo(()=>{
        var w = report?.w?.leads ?? 0;
        if(showOther || !numLeads)
           return w;
        const total = (report?.d?.leads ?? 0) + (report?.m?.leads ?? 0) + (report?.c?.leads ?? 0);
        if(total + w == numLeads)
            return w
        return numLeads - total
    }, [numLeads, showOther, report]);


    return <div className="d-flex flex-wrap my-4">
        <TypeCounter className="whatsapp" icon="fab fa-whatsapp" title="WhatsApp" value={wa} />
        <TypeCounter className="direct" icon="far fa-paper-plane" title="Direct" value={report?.d?.leads ?? 0} />
        <TypeCounter className="messenger" icon="fab fa-facebook-messenger" title="Messenger" value={report?.m?.leads ?? 0} />
        <TypeCounter className="cadastro" icon="far fa-file-lines" title="Cadastro" value={report?.c?.leads ?? 0} />
        {showOther && <TypeCounter className="other" icon="fa fa-circle" title="Outros" value={report?.nd?.leads ?? 0} />}
    </div>
}

export function ChannelAccountDetail({account}:{account: FullReportEntry}){
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<AccountChannelReport>(null)
    const {period} = useContext(AccountContext);

    useEffect(()=>{
        (async function(){
            var data = await loadChannelReportByAccount(account.id, period)
            setData(data);
            setLoading(false);
        })();
    }, [])

    const leadSeries = useMemo(()=>{
        if(data == null) return;
        var list = asAccountDateReport(data);
        return channels.map(c => buildChannelSerie(list, c, "leads")).filter(s => sum(s.data, d => d.y) > 0)
    }, [data]);

    const costSeries = useMemo(()=>{
        if(data == null) return;
        var list = asAccountDateReport(data);
        return channels.map(c => buildChannelSerie(list, c, "spend")).filter(s => sum(s.data, d => d.y) > 0)
    }, [data]);

    const cplSeries = useMemo(()=>{
        if(data == null) return;
        var list = asAccountDateReport(data);
        return channels.map(c => buildChannelSerie(list, c, r => !r?.spend ? 0 : r.clicks / r.spend )).filter(s => sum(s.data, d => d.y) > 0)
    }, [data]);

    useEffect(function(){
        logEvent({type: "account_channel_report", params: {period, ...getAccountEventInfo(account)}})
    }, [account])

    return <div className="p-3 channel-report-details" style={{maxWidth:"1000px"}}>
        <h3>{account.name}</h3>
        <PeriodLabel />

        {loading && <Loading /> }

        {data != null && <TypeSummary report={data.summary} />}

        <h4>Leads</h4>
        {leadSeries && <LineChart data={leadSeries} height={160} xLabelFunction={xLabel} yLabelFunction={bigNumber} createTooltip={leadTooltip} />}

        <h4 className="mt-4">Investimento</h4>
        {leadSeries && <LineChart data={costSeries} height={160} xLabelFunction={xLabel} yLabelFunction={formatMoney} createTooltip={costTooltip} />}

        <h4 className="mt-4">CPL</h4>
        {leadSeries && <LineChart data={cplSeries} height={160} xLabelFunction={xLabel} yLabelFunction={formatMoney} createTooltip={cplTooltip} />}
    </div>
}